// ─── lessonData.jsx ───────────────────────────────────────────────────────────
// Lesson content for all training modules.
// Each module key maps to an array consumed by LessonPlayer.
//
// Lesson shape:
//   { id, title, type: "concept"|"calculator"|"quiz", accentColor?, content }
//
// Calculator lessons need: content.fields[], content.compute(vals), content.renderResults(results)
// Quiz lessons need:       content.questions[], content.passPct (0–1)
// ─────────────────────────────────────────────────────────────────────────────

// ── Shared helpers ────────────────────────────────────────────────────────────

function calcPI(loan, rate, years) {
  if (!loan || !rate || !years) return 0;
  const r = rate / 100 / 12, n = years * 12;
  if (r === 0) return loan / n;
  return (loan * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
}

function fmt$(n) {
  if (n >= 1000000) return `$${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000)    return `$${(n / 1000).toFixed(1)}k`;
  return `$${Math.round(n)}`;
}

// ResultPanel renders inside calculator lessons.
// content.renderResults must return a plain object: { highlight, rows, tip }
// LessonPlayer renders it — no JSX needed here.
// Format: { highlight: {label,value,color,barPct?,barLabels?}, rows: [{label,value,color?}], tip }
// ─────────────────────────────────────────────────────────────────────────────


// ════════════════════════════════════════════════════════════════════════════
// LOAN PRODUCTS
// ════════════════════════════════════════════════════════════════════════════

// ─────────────────────────────────────────────────────────────
// BRIDGE / FIX & FLIP
// ─────────────────────────────────────────────────────────────
export const tm_bridge = [
  {
    id: "br_intro",
    title: "What Is a Bridge Loan?",
    type: "concept",
    content: {
      text: `
Bridge loans are short-term, asset-based loans designed for real estate investors purchasing and renovating properties.

They are typically used for:
• Fix & flip projects
• Cash-out refinance for renovations
• Time-sensitive acquisitions

Key advantage: SPEED and flexibility vs traditional banks.
      `,
    },
  },
  {
    id: "br_metrics",
    title: "Core Metrics: LTV, LTC, ARV",
    type: "concept",
    content: {
      text: `
LTV (Loan-to-Value): Loan ÷ current property value  
LTC (Loan-to-Cost): Loan ÷ total project cost  
ARV (After Repair Value): Estimated value after rehab

Example:
Purchase = $200k  
Rehab = $50k  
ARV = $350k  

A lender might offer:
• 85% LTC = $212,500  
• 70% ARV cap = $245,000  

Lowest of the two wins.
      `,
    },
  },
  {
    id: "br_draws",
    title: "Rehab Draw Process",
    type: "concept",
    content: {
      text: `
Rehab funds are not given upfront.

Process:
1. Borrower completes work
2. Requests draw
3. Inspector verifies progress
4. Lender releases funds

This protects the lender and ensures project completion.
      `,
    },
  },
  {
    id: "br_risk",
    title: "What Lenders Look For",
    type: "concept",
    content: {
      text: `
Key underwriting factors:

• Borrower experience (track record)
• Liquidity (reserves)
• Deal quality (margin between cost and ARV)
• Exit strategy (flip vs refinance)

Red flags:
• Thin margins
• First-time investors
• Overleveraged deals
      `,
    },
  },
  {
    id: "br_sales",
    title: "How to Sell Bridge Loans",
    type: "concept",
    content: {
      text: `
Positioning:

You're not selling rate — you're selling SPEED and EXECUTION.

Use this framing:
“If you miss the deal, rate doesn’t matter.”

Focus on:
• Fast closings (7–14 days)
• High leverage
• Certainty of execution
      `,
    },
  },
];

export const tm_groundup = [
  {
    id: "gu_intro",
    title: "Ground-Up Construction Basics",
    type: "concept",
    content: {
      text: `
Ground-up loans finance construction from land purchase to completion.

These deals are higher risk but higher return.
      `,
    },
  },
  {
    id: "gu_budget",
    title: "Construction Budget & LTC",
    type: "concept",
    content: {
      text: `
Lenders evaluate:

• Land cost
• Hard costs (construction)
• Soft costs (permits, fees)

Typical leverage:
• 80–90% LTC
• 65–70% ARV cap
      `,
    },
  },
  {
    id: "gu_draws",
    title: "Draw Schedule",
    type: "concept",
    content: {
      text: `
Funds released in stages:

• Foundation
• Framing
• Dry-in
• Finish

Each requires inspection.
      `,
    },
  },
  {
    id: "gu_risk",
    title: "Major Risks",
    type: "concept",
    content: {
      text: `
• Cost overruns
• Delays
• Market shifts

This is why experience matters heavily.
      `,
    },
  },
  {
    id: "gu_sales",
    title: "Selling Ground-Up Deals",
    type: "concept",
    content: {
      text: `
Focus on:

• Builder experience
• Profit margins
• Exit strategy (sell vs rent)

These are larger, higher-commission deals.
      `,
    },
  },
];

// ── tm_multifamily: Multifamily 1–4 Units ────────────────────────────────────
export const tm_multifamily = [
  {
    id: "mf_what",
    title: "Multifamily 1–4 Overview",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "1–4 unit properties: the most versatile product in our lineup.",
      paragraphs: [
        "Properties with 1–4 residential units are classified as <strong>residential</strong> by Fannie/Freddie — which means they can be financed with DSCR loans, bridge loans, or conventional products depending on borrower goals.",
        "Investors targeting small residential portfolios (single-family, duplexes, triplexes, quads) are your most common repeat borrowers. Learn to speak portfolio, not transaction.",
      ],
      breakdown: [
        { letter: "1", word: "SFR (1 unit)",  desc: "Single-family rental — simplest DSCR underwriting, widest product access" },
        { letter: "2", word: "Duplex",        desc: "Two units — one lease per unit, combined rent for DSCR calculation" },
        { letter: "3", word: "Triplex",       desc: "Three units — requires individual leases; DSCR 1-4 still applies" },
        { letter: "4", word: "Quad",          desc: "Four units — highest residential leverage before crossing into commercial" },
      ],
      callout: { type: "tip", text: "A quad (4-unit) with strong DSCR is one of the best deals you can submit — four rent streams, residential rates, and high investor demand. Always ask if they're open to larger properties." },
    },
  },
  {
    id: "mf_products",
    title: "Product Fit by Goal",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Same property type, different products — match to the borrower's goal.",
      programs: [
        { name: "Bridge Loan", badge: "Buy & Renovate", color: "#0ea5e9", ltv: "Up to 75% ARV", min: "6–24 months · Interest only", desc: "Investor buying a distressed 2–4 unit to renovate and either sell or refi into a term loan. Use when property isn't rent-ready." },
        { name: "DSCR Term Loan", badge: "Buy & Hold", color: "#16a34a", ltv: "Up to 85% LTV", min: "30yr · DSCR ≥ 1.0 · 660+ credit", desc: "Stabilized property with tenants in place. Qualifies on rental income — no income docs. Best product for long-term holds." },
        { name: "No Ratio Term", badge: "Cash-Flow Negative", color: "#f97316", ltv: "Up to 75% LTV", min: "No DSCR floor · 660+ credit", desc: "Property doesn't cash flow at current rents. Removes DSCR requirement at the cost of lower leverage and higher rate." },
        { name: "Credit Flex", badge: "Credit Challenged", color: "#dc2626", ltv: "Up to 65% LTV", min: "Sub-660 credit OK · Last resort", desc: "Borrower has challenged credit. Use only when no other program fits." },
      ],
      callout: { type: "tip", text: "Ask 'is the property currently rented?' on every multifamily call. The answer tells you immediately whether you're looking at a bridge deal or a DSCR deal." },
    },
  },
  {
    id: "mf_dscr",
    title: "DSCR on Multifamily",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "DSCR works the same — just add all rent streams together.",
      paragraphs: [
        "For 2–4 unit properties, DSCR uses <strong>combined gross monthly rent</strong> from all units divided by the total PITIA on the loan.",
        "Each unit needs its own executed lease. If a unit is vacant, some lenders will use a market rent estimate — but expect a haircut on the vacancy.",
      ],
      examples: [
        { name: "Duplex — Strong DSCR", emoji: "🏘️", color: "#16a34a", bg: "#f0fdf4", detail: "Unit A: $1,400/mo · Unit B: $1,350/mo · Combined: $2,750", dscr: 1.32, outcome: "PITIA $2,083 (P&I $1,600 + Tax $283 + Ins $200 + HOA $0). DSCR 1.32 — qualifies for DSCR 1-4 at full leverage. Strong deal." },
        { name: "Quad — Partial Vacancy", emoji: "🏗️", color: "#f97316", bg: "#fff7ed", detail: "3 of 4 units rented @ $1,100 each = $3,300/mo", dscr: 0.94, outcome: "PITIA $3,500. DSCR 0.94 — below 1.0 but above 0.75. If borrower has 700+ credit, DSCR 1-4 at lower LTV still works. Otherwise No Ratio Term." },
      ],
      callout: { type: "warning", text: "Vacancy is the DSCR killer on multifamily. Always ask how many units are rented and get leases for each. One vacant unit in a quad can drop DSCR from 1.25 to 0.93." },
    },
  },
  {
    id: "mf_calc",
    title: "Multifamily DSCR Calculator",
    type: "calculator",
    accentColor: "#2563eb",
    content: {
      headline: "Calculate DSCR across up to 4 units.",
      intro: "Enter rent for each occupied unit. Leave vacant units at $0.",
      fields: [
        { key: "rent1",    label: "Unit 1 Rent ($)",     default: 1400,  step: 50 },
        { key: "rent2",    label: "Unit 2 Rent ($)",     default: 1350,  step: 50 },
        { key: "rent3",    label: "Unit 3 Rent ($)",     default: 0,     step: 50 },
        { key: "rent4",    label: "Unit 4 Rent ($)",     default: 0,     step: 50 },
        { key: "loan",     label: "Loan Amount ($)",     default: 320000, step: 5000 },
        { key: "rate",     label: "Interest Rate (%)",   default: 7.5,   step: 0.125, min: 3 },
        { key: "years",    label: "Term (years)",        default: 30,    step: 1 },
        { key: "taxes",    label: "Taxes/mo ($)",        default: 350,   step: 25 },
        { key: "ins",      label: "Insurance/mo ($)",    default: 200,   step: 25 },
        { key: "credit",   label: "Borrower Credit",     default: 720,   step: 10, min: 500 },
      ],
      compute(v) {
        const totalRent = v.rent1 + v.rent2 + v.rent3 + v.rent4;
        const units     = [v.rent1, v.rent2, v.rent3, v.rent4].filter(r => r > 0).length;
        const pi        = calcPI(v.loan, v.rate, v.years);
        const pitia     = pi + v.taxes + v.ins;
        const dscr      = pitia > 0 ? totalRent / pitia : 0;
        const barPct    = Math.min(dscr / 2, 1) * 100;
        const color     = dscr >= 1.25 ? "#16a34a" : dscr >= 1.0 ? "#ca8a04" : dscr >= 0.75 ? "#f97316" : "#dc2626";
        let prog;
        if (dscr >= 1.0 && v.credit >= 660)      prog = "DSCR 1-4 — Best Rates · 85% LTV";
        else if (dscr >= 0.75 && v.credit >= 700) prog = "DSCR 1-4 (lower LTV) · 80% max";
        else if (v.credit < 660)                  prog = "Credit Flex · 65% LTV";
        else                                       prog = "No Ratio Term · 75% LTV";
        const tip = dscr >= 1.25 ? "Strong cash flow. Submit with confidence."
          : dscr >= 1.0 ? "Qualifies for standard DSCR 1-4."
          : dscr >= 0.75 ? "Below 1.0 — check credit score to determine program."
          : "DSCR below 0.75. No Ratio or Credit Flex only.";
        return { totalRent, units, pi, pitia, dscr, barPct, color, prog, tip };
      },
      renderResults(r) {
        return {
          highlight: { label: "DSCR", value: r.dscr.toFixed(2), color: r.color, barPct: r.barPct, barLabels: ["0", "1.25 sweet spot", "2.0+"] },
          rows: [
            { label: `Combined Rent (${r.units} units)`, value: `$${Math.round(r.totalRent)}/mo` },
            { label: "Monthly PITIA",  value: `$${Math.round(r.pitia)}/mo` },
            { label: "P&I",            value: `$${Math.round(r.pi)}/mo` },
            { label: "Program",        value: r.prog, color: r.color },
          ],
          tip: r.tip,
        };
      },
      callout: { type: "warning", text: "This calculator excludes HOA. If the property has association dues, add them to the taxes/insurance field or the DSCR will be overstated." },
    },
  },
  {
    id: "mf_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#2563eb",
    content: {
      passPct: 0.75,
      questions: [
        { id: "mq1", question: "How is DSCR calculated for a duplex?", options: ["Use only the higher-paying unit's rent", "Use average rent of all units", "Use combined gross rent from both units divided by PITIA", "Use 75% of combined rent as a vacancy buffer"], correct: 2, explanation: "Combined gross monthly rent from all occupied units ÷ total PITIA. Each unit needs an executed lease." },
        { id: "mq2", question: "An investor is buying a vacant triplex to renovate. Which product fits?", options: ["DSCR Term Loan", "Bridge Loan", "No Ratio Term", "Credit Flex"], correct: 1, explanation: "The property isn't rent-ready, so a DSCR loan (which requires rental income) doesn't work. Bridge loan is the right fit — renovate, stabilize, then refi into DSCR." },
        { id: "mq3", question: "What is the maximum residential unit count before a property becomes commercial?", options: ["2 units", "4 units", "6 units", "10 units"], correct: 1, explanation: "1–4 units = residential classification. 5+ units = commercial, requiring different loan products and underwriting." },
        { id: "mq4", question: "A quad has 3 units rented at $1,100/mo each. PITIA is $3,500. What is the DSCR?", options: ["0.83", "0.94", "1.10", "1.26"], correct: 1, explanation: "3 units × $1,100 = $3,300 combined rent. $3,300 ÷ $3,500 = 0.943, so approximately 0.94." },
      ],
    },
  },
];

// ── tm_dscr: DSCR Term Loans ──────────────────────────────────────────────────
export const tm_dscr = [
  {
    id: "dscr_intro",
    title: "What Is DSCR?",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "The property qualifies itself — no tax returns needed.",
      paragraphs: [
        "DSCR stands for <strong>Debt Service Coverage Ratio</strong>. Instead of verifying the borrower's income, we verify whether the rental income covers the loan payment. No W-2, no paystubs, no tax returns.",
        "For self-employed investors and landlords — which is most of your borrowers — this is a game-changer.",
      ],
      formula: { label: "The Formula", expression: "DSCR = Monthly Rent ÷ PITIA", sub: "PITIA = Principal + Interest + Taxes + Insurance + Association dues" },
      callout: { type: "tip", text: "Practice this 10-second pitch: 'If the rent covers the payment by 25%, you qualify — no tax returns.' That's DSCR in one sentence." },
    },
  },
  {
    id: "dscr_pitia",
    title: "The PITIA Breakdown",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "Every component of PITIA must be included — missing one kills the deal.",
      breakdown: [
        { letter: "P", word: "Principal", desc: "The portion reducing the loan balance each month" },
        { letter: "I", word: "Interest", desc: "The cost of borrowing — typically the largest component" },
        { letter: "T", word: "Taxes", desc: "Annual property taxes ÷ 12" },
        { letter: "I", word: "Insurance", desc: "Hazard/homeowner's insurance ÷ 12" },
        { letter: "A", word: "Association Dues", desc: "HOA, condo fees, co-op maintenance (if applicable)" },
      ],
      callout: { type: "warning", text: "HOA is the #1 DSCR killer. A $400/mo HOA on a condo can drop a clean 1.25 DSCR to 0.95. Always ask about association dues before quoting DSCR." },
    },
  },
  {
    id: "dscr_thresholds",
    title: "DSCR Thresholds & Programs",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "Four zones, four programs. Always start at the top.",
      thresholds: [
        { range: "DSCR 1.25+", label: "Sweet spot 🎯", color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", desc: "Best rates, full leverage. DSCR 1-4 at up to 85% LTV. This is where you want every deal." },
        { range: "DSCR 1.0 – 1.24", label: "Qualifies", color: "#ca8a04", bg: "#fffbeb", border: "#fde68a", desc: "Standard DSCR 1-4 territory. Qualifies at full LTV. Pricing improves as DSCR rises." },
        { range: "DSCR 0.75 – 0.99", label: "Marginal", color: "#f97316", bg: "#fff7ed", border: "#fed7aa", desc: "700+ credit: DSCR 1-4 still works at lower LTV (80% max). Under 700: No Ratio Term." },
        { range: "DSCR < 0.75", label: "Below floor", color: "#dc2626", bg: "#fef2f2", border: "#fecaca", desc: "No Ratio Term or Credit Flex only. Very limited leverage." },
      ],
      programs: [
        { name: "DSCR 1-4 Unit", badge: "Default", color: "#16a34a", ltv: "85% LTV", min: "DSCR ≥ 1.0 · 660+ credit", desc: "Always try this first. Best rates and highest leverage for standard 1–4 unit rentals." },
        { name: "DSCR Multi (5–9)", badge: "Larger", color: "#2563eb", ltv: "75% LTV", min: "DSCR ≥ 1.0 · 660+ credit · Rent roll + T12", desc: "Requires full rent roll and T12 operating statement. Same DSCR logic, more documentation." },
        { name: "No Ratio Term", badge: "Fallback", color: "#f97316", ltv: "75% LTV", min: "No DSCR floor · 660+ credit", desc: "Removes DSCR requirement. Higher rates, lower leverage. Use when property doesn't cash flow." },
        { name: "Credit Flex", badge: "Last resort", color: "#dc2626", ltv: "65% LTV", min: "Sub-660 credit OK", desc: "Safety net for challenged credit. Highest rates, lowest leverage." },
      ],
      callout: { type: "tip", text: "Always funnel top-down: DSCR 1-4 → No Ratio → Credit Flex. Never lead with a fallback." },
    },
  },
  {
    id: "dscr_rates",
    title: "Rate Adjustments",
    type: "concept",
    accentColor: "#16a34a",
    content: {
      headline: "The base rate is just a starting point — every risk factor stacks.",
      breakdown: [
        { letter: "↑", word: "LTV", desc: "Higher LTV = higher rate. 80% costs more than 70%." },
        { letter: "↑", word: "Credit Score", desc: "700 costs more than 760+. Each bracket moves the needle." },
        { letter: "↑", word: "Interest-Only", desc: "Choosing IO adds +0.25–0.50% to the rate." },
        { letter: "↓", word: "Prepay Penalty", desc: "5-year prepay = lower rate. No prepay = highest rate. Always show both." },
        { letter: "↑", word: "Property Type", desc: "Condos and 2–4 units cost more than SFRs." },
      ],
      callout: { type: "tip", text: "Always show borrowers two rate scenarios: one with short prepay, one with 5-year. That conversation builds trust and closes more deals than a single quote ever will." },
    },
  },
  {
    id: "dscr_calc",
    title: "DSCR Calculator",
    type: "calculator",
    accentColor: "#16a34a",
    content: {
      headline: "Run any rental deal in seconds.",
      intro: "Adjust any input — DSCR, PITIA, and program recommendation update instantly.",
      fields: [
        { key: "rent",   label: "Monthly Rent ($)",  default: 2500,   step: 50 },
        { key: "loan",   label: "Loan Amount ($)",   default: 300000, step: 5000 },
        { key: "rate",   label: "Rate (%)",          default: 7.5,    step: 0.125, min: 3 },
        { key: "years",  label: "Term (yrs)",        default: 30,     step: 1 },
        { key: "taxes",  label: "Taxes/mo ($)",      default: 200,    step: 25 },
        { key: "ins",    label: "Insurance/mo ($)",  default: 100,    step: 25 },
        { key: "hoa",    label: "HOA/mo ($)",        default: 0,      step: 50 },
        { key: "credit", label: "Credit Score",      default: 720,    step: 10, min: 500 },
        { key: "units",  label: "# Units",           default: 1,      step: 1, min: 1 },
      ],
      compute(v) {
        const pi    = calcPI(v.loan, v.rate, v.years);
        const pitia = pi + v.taxes + v.ins + v.hoa;
        const dscr  = pitia > 0 ? v.rent / pitia : 0;
        const color = dscr >= 1.25 ? "#16a34a" : dscr >= 1.0 ? "#ca8a04" : dscr >= 0.75 ? "#f97316" : "#dc2626";
        let prog;
        if (v.units >= 5)                             prog = dscr >= 1.0 && v.credit >= 660 ? "DSCR Multi (5–9) · 75% LTV" : "No Ratio Term · 75% LTV";
        else if (dscr >= 1.0 && v.credit >= 660)     prog = "DSCR 1-4 — Best Rates · 85% LTV";
        else if (dscr >= 0.75 && v.credit >= 700)    prog = "DSCR 1-4 (lower LTV) · 80% max";
        else if (v.credit < 660)                     prog = "Credit Flex · 65% LTV";
        else                                          prog = "No Ratio Term · 75% LTV";
        const tip = dscr >= 1.25 ? "Sweet spot. Best rates available."
          : dscr >= 1.0 ? "Qualifies for DSCR 1-4. Check LTV and credit for rate adjustments."
          : dscr >= 0.75 ? "Marginal. 700+ credit may still qualify for DSCR 1-4 at lower LTV."
          : "Below floor. No Ratio or Credit Flex only. Consider a larger down payment.";
        return { pi, pitia, dscr, color, prog, tip, barPct: Math.min(dscr / 2, 1) * 100 };
      },
      renderResults(r) {
        return {
          highlight: { label: "DSCR", value: r.dscr.toFixed(2), color: r.color, barPct: r.barPct, barLabels: ["0", "1.25 sweet spot", "2.0+"] },
          rows: [
            { label: "Monthly PITIA", value: `$${Math.round(r.pitia)}` },
            { label: "P&I",           value: `$${Math.round(r.pi)}` },
            { label: "Program",       value: r.prog, color: r.color },
          ],
          tip: r.tip,
        };
      },
    },
  },
  {
    id: "dscr_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#16a34a",
    content: {
      passPct: 0.75,
      questions: [
        { id: "dq1", question: "What does DSCR measure?", options: ["Borrower income vs debt", "Rental income vs total monthly loan payment", "Property value vs purchase price", "Equity in the deal vs loan amount"], correct: 1, explanation: "DSCR = Monthly Rent ÷ PITIA. It measures whether the property's rental income covers the full loan payment." },
        { id: "dq2", question: "A condo rents for $2,200/mo. PITIA is $1,960 (including $380 HOA). DSCR?", options: ["1.12", "1.25", "0.97", "1.04"], correct: 0, explanation: "$2,200 ÷ $1,960 = 1.122. The HOA must be included or you'd have PITIA of $1,580 and DSCR of 1.39 — overstate." },
        { id: "dq3", question: "Borrower has DSCR 0.85, credit score 715. Which program?", options: ["Credit Flex", "No Ratio Term", "DSCR 1-4 at lower LTV (max 80%)", "Does not qualify for any program"], correct: 2, explanation: "DSCR 0.75–0.99 + 700+ credit = DSCR 1-4 still works at lower LTV (80% max) and slightly higher rate." },
        { id: "dq4", question: "Why does a longer prepay penalty reduce the interest rate?", options: ["It reduces the lender's risk of default", "The lender is guaranteed a longer income stream", "Longer prepay means lower LTV", "It's a regulatory requirement"], correct: 1, explanation: "A 5-year prepay guarantees the lender income for 5 years. That certainty = lower rate. No prepay = higher rate to compensate for early payoff risk." },
      ],
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// SALES & CLOSING
// ════════════════════════════════════════════════════════════════════════════

// ── tm_discovery: The Discovery Call ─────────────────────────────────────────
export const tm_discovery = [
  {
    id: "dc_intro",
    title: "The 20-Minute Discovery Call",
    type: "concept",
    content: {
      text: `
Goal: qualify, structure, and move to next step FAST.
      `,
    },
  },
  {
    id: "dc_questions",
    title: "Key Questions",
    type: "concept",
    content: {
      text: `
Ask:

• What’s the deal?
• Purchase price?
• Rehab budget?
• Exit strategy?
• Timeline?

You’re gathering underwriting data.
      `,
    },
  },
  {
    id: "dc_control",
    title: "Control the Call",
    type: "concept",
    content: {
      text: `
Don’t let borrowers ramble.

Guide the conversation like an underwriter.
      `,
    },
  },
];

// ════════════════════════════════════════════════════════════════════════════
// COMPANY & TOOLS
// ════════════════════════════════════════════════════════════════════════════

// ── tm_company: Propertunity 101 ──────────────────────────────────────────────
export const tm_company = [
  {
    id: "co_intro",
    title: "Who We Are",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Propertunity Capital: the lender that says yes when banks say no.",
      paragraphs: [
        "Propertunity Capital is a real-estate lending platform connecting investors with bridge loans, DSCR term loans, ground up construction, and multifamily financing across Florida and beyond.",
        "Our partnership with <strong>Lem Lux Realty</strong> puts us inside the deal flow — brokers, flippers, and landlords all move through the same ecosystem.",
      ],
      callout: { type: "tip", text: "One-sentence company pitch: 'We're the lender that closes investment deals in 7 days — no income docs, no bank process.' Memorize it." },
    },
  },
  {
    id: "co_role",
    title: "Your Role",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Qualify. Educate. Hand off a clean file.",
      breakdown: [
        { letter: "1", word: "Qualify", desc: "Determine if the deal and borrower meet our basic criteria before spending anyone's time" },
        { letter: "2", word: "Educate", desc: "Match the borrower to the right product and explain why it fits their goals" },
        { letter: "3", word: "Hand Off", desc: "Submit a complete file to underwriting — all four data points ready" },
      ],
      callout: { type: "warning", text: "Incomplete files are the #1 cause of delays and the #1 reason deals fall through. Never submit without ARV, purchase+rehab, track record, and exit strategy." },
    },
  },
  {
    id: "co_culture",
    title: "Culture & Expectations",
    type: "concept",
    accentColor: "#2563eb",
    content: {
      headline: "Fast, clear, professional — every time.",
      list: [
        "<strong>24-hour follow-up</strong> — no exceptions. Speed of response signals professionalism",
        "<strong>CRM updated after every interaction</strong> — if it's not logged, it didn't happen",
        "No deal is too small to treat with full professionalism",
        "Escalate compliance questions immediately — never guess",
        "Under-promise on timelines, over-deliver on execution",
      ],
      callout: { type: "tip", text: "The reps who hit quota aren't always the best salespeople — they're the most consistent. Habits beat talent every time." },
    },
  },
  {
    id: "co_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#2563eb",
    content: {
      passPct: 0.75,
      questions: [
        { id: "coq1", question: "What is the sales rep's three-part role?", options: ["Pitch, close, fund", "Qualify, educate, hand off to underwriting", "Source, underwrite, close", "Call, follow up, collect"], correct: 1, explanation: "Qualify the borrower, educate them on the right product, and hand off a complete file to underwriting." },
        { id: "coq2", question: "What is the follow-up standard?", options: ["48 hours", "72 hours", "Within 24 hours", "By end of week"], correct: 2, explanation: "24 hours — no exceptions. Speed of follow-up is directly correlated with deal conversion." },
        { id: "coq3", question: "What are the four things needed before submitting to underwriting?", options: ["Credit, income, LTV, appraisal", "ARV, purchase+rehab, track record, exit strategy", "Deal type, property type, loan term, rate", "Name, address, loan amount, down payment"], correct: 1, explanation: "ARV, purchase+rehab budget, borrower track record, and a defined exit strategy." },
        { id: "coq4", question: "Who is Propertunity's primary referral partner?", options: ["Chase Bank", "Marcus & Millichap", "Lem Lux Realty", "CBRE"], correct: 2, explanation: "Lem Lux Realty is our primary partner, putting us at the center of deal flow." },
      ],
    },
  },
];

// ── tm_crm: CRM Mastery ───────────────────────────────────────────────────────
export const tm_crm = [
  {
    id: "crm_stages",
    title: "Pipeline Stages",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Five stages. A lead should never stall more than 5 business days without a logged action.",
      breakdown: [
        { letter: "1", word: "New Lead",        desc: "Just entered the system. Contact within 24 hours." },
        { letter: "2", word: "Contacted",        desc: "First outreach made. Waiting on discovery call." },
        { letter: "3", word: "Qualified",        desc: "Discovery call done. Deal fits our criteria." },
        { letter: "4", word: "Term Sheet Sent",  desc: "Formal offer out. Follow up every 2 days until decision." },
        { letter: "5", word: "Funded",           desc: "Deal closed. Trigger referral ask and portfolio follow-up." },
      ],
      callout: { type: "warning", text: "A lead stalled in the same stage for 5+ business days with no activity logged is a red flag managers review weekly." },
    },
  },
  {
    id: "crm_logging",
    title: "Activity Logging",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Log every call, email, and text within 2 hours. Always.",
      list: [
        "Record the <strong>outcome</strong> — not just that the interaction happened",
        "Always log the <strong>next step</strong> with a specific date and action",
        "Managers review logs weekly — clean logs reflect professional habits",
        "If it's not in the CRM, it didn't happen",
        "Use call notes to capture borrower priorities — they inform every future pitch",
      ],
      callout: { type: "tip", text: "Reps who log same-day have 30% higher conversion. The notes become pitch prep for the next call." },
    },
  },
  {
    id: "crm_cadence",
    title: "Follow-Up Cadence",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Persistence with professionalism beats a perfect pitch.",
      table: {
        headers: ["Day", "Action", "Goal"],
        rows: [
          ["Day 1",  "Phone call",          "Warm intro, needs assessment"],
          ["Day 3",  "Email",               "Recap value, send resources"],
          ["Day 7",  "Call + text",         "Check for questions, create urgency"],
          ["Day 14", "Breakup email",       "Re-engage or close the loop"],
          ["Day 30+","Monthly newsletter",  "Stay top of mind for future deals"],
        ],
      },
      callout: { type: "tip", text: "The Day 14 breakup email gets more responses than almost any other touchpoint. Try: 'I don't want to keep bothering you — if timing's off, no worries. Just let me know.' It works." },
    },
  },
  {
    id: "crm_forecast",
    title: "Forecasting & Weekly Review",
    type: "concept",
    accentColor: "#ca8a04",
    content: {
      headline: "Review every Monday. Your forecast is only as good as your data.",
      list: [
        "Identify deals that could close <strong>this month</strong> and assign one specific action to each",
        "Flag stalled deals: push harder or disqualify and move on",
        "Update probabilities based on actual deal status — not wishful thinking",
        "Your weekly forecast is reviewed by your manager — make it accurate",
        "Garbage in, garbage out. A padded pipeline helps no one",
      ],
      callout: { type: "warning", text: "Inflating your forecast to look good is a career-shortener. An accurate pipeline — even a small one — earns more trust than a padded one." },
    },
  },
  {
    id: "crm_quiz",
    title: "Knowledge Check",
    type: "quiz",
    accentColor: "#ca8a04",
    content: {
      passPct: 0.75,
      questions: [
        { id: "crmq1", question: "How long can a lead sit in one stage without a logged activity?", options: ["10 business days", "7 calendar days", "5 business days", "30 days"], correct: 2, explanation: "5 business days is the maximum. Beyond that, the lead is flagged in manager reviews." },
        { id: "crmq2", question: "What must every logged activity include beyond the interaction itself?", options: ["Deal's estimated close date", "Outcome + next step with a specific date", "Borrower's credit score", "Loan amount and property address"], correct: 1, explanation: "Every log needs the outcome (what happened) and the next step (what happens next, with a date)." },
        { id: "crmq3", question: "What is the purpose of the Day 14 breakup email?", options: ["Remove the lead from CRM", "Re-engage silent leads or close the loop", "Escalate to a manager", "Send a final term sheet"], correct: 1, explanation: "The breakup email often re-engages leads who've gone quiet. It respects their time and frequently prompts a response." },
        { id: "crmq4", question: "When should you log an activity after a call?", options: ["End of the day", "Within 2 hours", "By end of week", "Before your next call"], correct: 1, explanation: "Within 2 hours while details are fresh. Same-day logging correlates with 30% higher conversion." },
      ],
    },
  },
];

// ── tm_compliance: Compliance & Fair Lending ──────────────────────────────────
export const tm_compliance = [
  {
    id: "cmp_respa",
    title: "RESPA Overview",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "RESPA governs fees and referrals. Never accept anything of value for a referral.",
      paragraphs: [
        "The <strong>Real Estate Settlement Procedures Act (RESPA)</strong> requires transparent disclosure of all settlement costs and prohibits kickbacks for referrals.",
        "Any affiliated business arrangement — like our Lem Lux Realty partnership — must be disclosed in writing to the borrower before closing.",
      ],
      list: [
        "Never accept or give cash, gifts, or anything of value in exchange for a referral",
        "All affiliated business arrangements must be disclosed in writing at or before application",
        "RESPA violations carry civil and criminal penalties",
        "Escalate any referral arrangement questions to compliance immediately",
      ],
      callout: { type: "warning", text: "If a referral partner suggests any kind of 'thank you' arrangement, stop the conversation immediately and report it to your manager. RESPA violations are not a gray area." },
    },
  },
  {
    id: "cmp_fair",
    title: "Fair Lending Laws",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "Every borrower is evaluated on identical criteria. No exceptions.",
      paragraphs: [
        "The <strong>Equal Credit Opportunity Act (ECOA)</strong> and <strong>Fair Housing Act</strong> prohibit discrimination based on race, color, religion, national origin, sex, familial status, or disability.",
      ],
      list: [
        "Never quote different rates or terms to comparable borrowers based on protected characteristics",
        "Steering borrowers to certain products based on protected class is illegal",
        "Document every credit decision with objective, deal-based criteria only",
        "Disparate impact — unintentional discrimination through neutral-looking policies — is also actionable",
      ],
      callout: { type: "warning", text: "You don't have to intend to discriminate to be liable. If a pattern of decisions disproportionately affects a protected class, that is a fair lending violation." },
    },
  },
  {
    id: "cmp_privacy",
    title: "Privacy & Data Handling",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "Borrower data is confidential. Treat it that way.",
      list: [
        "Never share loan details, income, or credit info outside of the deal team",
        "Never discuss deals in public spaces or over unsecured channels",
        "All borrower data must be stored only in approved platforms (CRM + secure file sharing)",
        "If you suspect a data breach, report it to your manager immediately",
      ],
      callout: { type: "warning", text: "Mentioning a borrower's deal by name — even casually — to someone outside the deal team is a privacy violation. This includes conversations with other reps." },
    },
  },
  {
    id: "cmp_dos",
    title: "Do's and Don'ts",
    type: "concept",
    accentColor: "#dc2626",
    content: {
      headline: "The non-negotiables.",
      examples: [
        { name: "DO ✅", emoji: "✅", color: "#16a34a", bg: "#f0fdf4", outcome: "Document everything in the CRM. Escalate compliance questions immediately. Get everything in writing. Know your RESPA disclosure obligations before closing." },
        { name: "DON'T ❌", emoji: "❌", color: "#dc2626", bg: "#fef2f2", outcome: "Make promises about rates or approvals before underwriting. Accept cash, gifts, or off-platform payments. Quote different terms to comparable borrowers. Discuss deals outside secure channels." },
      ],
      callout: { type: "warning", text: "When in doubt, don't. Escalate immediately. A 5-minute conversation with your manager today is better than a compliance issue next month." },
    },
  },
  {
    id: "cmp_quiz",
    title: "Required Knowledge Check",
    type: "quiz",
    accentColor: "#dc2626",
    content: {
      passPct: 1.0,
      intro: "This is a required module. You must answer all 4 questions correctly to pass.",
      questions: [
        { id: "cmpq1", question: "What does RESPA prohibit?", options: ["Charging origination fees", "Kickbacks — giving or receiving anything of value for referrals", "Working with affiliated businesses", "Disclosing settlement costs"], correct: 1, explanation: "RESPA prohibits kickbacks of any kind in exchange for referrals." },
        { id: "cmpq2", question: "Which of the following is a Fair Housing Act violation?", options: ["Requiring higher rates for lower credit scores", "Quoting different rates based on national origin", "Declining a loan based on DSCR", "Requiring income documentation"], correct: 1, explanation: "Charging different rates or terms based on protected characteristics (race, national origin, etc.) is prohibited." },
        { id: "cmpq3", question: "A referral partner offers to buy you dinner as a thank-you. You should:", options: ["Accept — it's just a dinner", "Accept only if under $50", "Decline and report the conversation to your manager", "Accept and disclose it in the CRM"], correct: 2, explanation: "Any gift or thing of value in a referral context is a RESPA violation. Decline and escalate." },
        { id: "cmpq4", question: "You suspect a colleague is quoting higher rates to certain borrowers based on national origin. You should:", options: ["Wait and monitor for a few more weeks", "Confront the colleague directly", "Report it to your manager or compliance team immediately", "Do nothing — it's not your responsibility"], correct: 2, explanation: "Fair lending violations must be reported immediately. You have an obligation to escalate." },
      ],
    },
  },
];

// ─── Master map ───────────────────────────────────────────────────────────────
export const LESSON_DATA_MAP = {
  tm_bridge:      tm_bridge,
  tm_groundup:    tm_groundup,
  tm_multifamily: tm_multifamily,
  tm_dscr:        tm_dscr,
  tm_discovery:   tm_discovery,
  tm_company:     tm_company,
  tm_crm:         tm_crm,
  tm_compliance:  tm_compliance,
};